import { AnimatePresence, motion } from 'framer-motion'
import { useState, type FormEvent } from 'react'
import { ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import { Icon } from '../components/Icon'
import { Logo, LogoMark } from '../components/Logo'
import { ShapeGlyph } from '../components/Shape'
import { Button, TextField } from '../components/ui'
import { LegalLinks } from './LegalScreen'

type Mode = 'login' | 'register' | 'confirm'

const MARQUEE_SHAPES = ['triangle', 'circle', 'star', 'hexagon', 'diamond', 'cross'] as const

/**
 * Mirrors the server rule in apps/server/src/auth, which in turn mirrors the
 * Cognito pool policy. Checked here purely so the feedback is instant - the
 * server still enforces it, since anything checked only in a browser is not
 * enforced at all.
 */
function passwordProblem(password: string): string | null {
  const missing: string[] = []
  if (password.length < 12) missing.push('at least 12 characters')
  if (!/[a-z]/.test(password)) missing.push('a lowercase letter')
  if (!/[A-Z]/.test(password)) missing.push('an uppercase letter')
  if (!/[0-9]/.test(password)) missing.push('a number')
  return missing.length > 0 ? `Needs ${missing.join(', ')}.` : null
}

export function AuthScreen() {
  const { login, register, confirm, resendCode } = useAuth()

  const [mode, setMode] = useState<Mode>('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  /**
   * Keyed by field name so a message lands under the input it is about. The
   * server tells us which field failed; rendering the same string under every
   * field puts "passwords need 12 characters" under the email box.
   */
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setFieldErrors({})
    setBusy(true)

    try {
      if (mode === 'login') {
        await login(email, password)
      } else if (mode === 'register') {
        const result = await register(email, password, displayName)
        if (result.needsConfirmation) {
          setMode('confirm')
          setNotice(`We sent a confirmation code to ${email}.`)
        }
      } else {
        await confirm(email, code)
        setNotice('Email confirmed. Sign in to play.')
        setMode('login')
        setCode('')
      }
    } catch (caught) {
      if (caught instanceof ApiError) {
        // Cognito reports password problems without naming a field, so fall
        // back to the error code rather than dropping the message on Email.
        const field =
          caught.field ??
          (caught.code === 'weak_password'
            ? 'password'
            : caught.code === 'invalid_code'
              ? 'code'
              : null)

        if (field) setFieldErrors({ [field]: caught.message })
        else setError(caught.message)
      } else {
        setError(caught instanceof Error ? caught.message : 'Something went wrong.')
      }
    } finally {
      setBusy(false)
    }
  }

  const switchTo = (next: Mode) => {
    setMode(next)
    setError(null)
    setFieldErrors({})
    setNotice(null)
  }

  return (
    <div className="grid min-h-dvh lg:grid-cols-[1.05fr_1fr]">
      {/* Brand side. Hidden on small screens where the form is the whole job. */}
      <aside className="relative hidden overflow-hidden border-r border-white/5 lg:flex lg:flex-col lg:justify-between lg:p-14">
        <Logo size="md" />

        <div className="relative z-10 max-w-lg">
          <h1 className="display-title text-[2.75rem] leading-[1.08] text-fg">
            Two players.
            <br />
            One pot.
            <br />
            <span className="text-lime">No second place.</span>
          </h1>
          <p className="mt-7 max-w-md text-[0.9375rem] leading-relaxed text-fg-muted">
            Ante your coins, pick a mode, and settle it in thirty seconds. Click faster than
            them, or count what they miss. The winner takes everything on the table.
          </p>

          <div className="mt-12 flex gap-6">
            {[
              { value: '3', label: 'Game modes' },
              { value: '2-4', label: 'Players' },
              { value: '30s', label: 'To settle it' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="font-mono text-2xl font-semibold tnum text-fg">{stat.value}</p>
                <p className="eyebrow mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* A slow drift of the shape vocabulary, so the brand side previews the
            game itself rather than showing decorative filler. */}
        <div aria-hidden className="pointer-events-none absolute inset-0 z-0">
          {MARQUEE_SHAPES.map((shape, index) => (
            <motion.div
              key={shape}
              className="absolute text-white/[0.035]"
              style={{
                left: `${8 + ((index * 37) % 82)}%`,
                top: `${12 + ((index * 53) % 74)}%`,
                width: 90 + (index % 3) * 46,
              }}
              animate={{ y: [0, -22, 0], rotate: [0, index % 2 === 0 ? 7 : -7, 0] }}
              transition={{
                duration: 13 + index * 2.4,
                repeat: Infinity,
                ease: 'easeInOut',
                delay: index * 0.7,
              }}
            >
              <ShapeGlyph shape={shape} />
            </motion.div>
          ))}
        </div>

        <div className="relative z-10">
          <p className="text-xs text-fg-dim">Practice coins are free and hold no cash value.</p>
          <LegalLinks className="mt-3" />
        </div>
      </aside>

      {/* Form side */}
      <main className="flex items-center justify-center px-5 py-12 pad-safe sm:px-8">
        <div className="w-full max-w-[26rem]">
          <div className="mb-10 flex justify-center lg:hidden">
            <Logo size="lg" />
          </div>

          <div className="mb-8 hidden lg:block">
            <LogoMark size={34} className="mb-5 text-lime" />
            <h2 className="display-title text-xl text-fg">
              {mode === 'login' ? 'Sign in' : mode === 'register' ? 'Create account' : 'Confirm email'}
            </h2>
            <p className="mt-2 text-sm text-fg-muted">
              {mode === 'login'
                ? 'Pick up where you left off.'
                : mode === 'register'
                  ? 'New players start with a stack of coins.'
                  : 'Enter the code we emailed you.'}
            </p>
          </div>

          <AnimatePresence mode="wait">
            <motion.form
              key={mode}
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
              className="space-y-5"
              noValidate
            >
              {mode === 'confirm' ? (
                <TextField
                  label="Confirmation code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="123456"
                  className="font-mono tracking-[0.4em]"
                  error={fieldErrors.code ?? null}
                  required
                />
              ) : (
                <>
                  {mode === 'register' && (
                    <TextField
                      label="Display name"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      autoComplete="nickname"
                      placeholder="How opponents see you"
                      hint="Leave blank and we'll build one from your email."
                      maxLength={24}
                    />
                  )}

                  <TextField
                    label="Email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                    inputMode="email"
                    placeholder="you@example.com"
                    icon={<Icon name="mail" size={17} />}
                    error={fieldErrors.email ?? null}
                    required
                  />

                  <TextField
                    label="Password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                    placeholder={mode === 'register' ? 'At least 12 characters' : '••••••••'}
                    hint={
                      mode === 'register'
                        ? '12 characters or more, with upper and lower case and a number.'
                        : undefined
                    }
                    icon={<Icon name="lock" size={17} />}
                    error={fieldErrors.password ?? null}
                    // Validated on blur, not on every keystroke: flagging a
                    // password as too short while it is still being typed is
                    // just nagging.
                    onBlur={() => {
                      if (mode !== 'register') return
                      const problem = password ? passwordProblem(password) : null
                      setFieldErrors((current) => {
                        const next = { ...current }
                        if (problem) next.password = problem
                        else delete next.password
                        return next
                      })
                    }}
                    required
                  />
                </>
              )}

              {error && (
                <motion.p
                  role="alert"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="flex items-start gap-2 rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger"
                >
                  <Icon name="close" size={16} className="mt-0.5 shrink-0" />
                  {error}
                </motion.p>
              )}

              {notice && (
                <p className="flex items-start gap-2 rounded-xl border border-lime/25 bg-lime-wash px-4 py-3 text-sm text-lime">
                  <Icon name="check" size={16} className="mt-0.5 shrink-0" />
                  {notice}
                </p>
              )}

              <Button type="submit" size="lg" fullWidth loading={busy}>
                {mode === 'login' ? 'Sign in' : mode === 'register' ? 'Create account' : 'Confirm'}
                {!busy && <Icon name="arrowRight" size={18} />}
              </Button>
            </motion.form>
          </AnimatePresence>

          <div className="mt-7 text-center text-sm text-fg-muted">
            {mode === 'login' && (
              <p>
                No account yet?{' '}
                <button
                  type="button"
                  onClick={() => switchTo('register')}
                  className="font-semibold text-lime underline-offset-4 hover:underline"
                >
                  Create one
                </button>
              </p>
            )}
            {mode === 'register' && (
              <p>
                Already playing?{' '}
                <button
                  type="button"
                  onClick={() => switchTo('login')}
                  className="font-semibold text-lime underline-offset-4 hover:underline"
                >
                  Sign in
                </button>
              </p>
            )}
            {mode === 'confirm' && (
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => {
                    resendCode(email).catch(() => setError('Could not resend the code.'))
                    setNotice('Sent another code.')
                  }}
                  className="font-semibold text-lime underline-offset-4 hover:underline"
                >
                  Resend code
                </button>
                <p>
                  <button
                    type="button"
                    onClick={() => switchTo('login')}
                    className="text-fg-dim underline-offset-4 hover:underline"
                  >
                    Back to sign in
                  </button>
                </p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
